import { useEffect, useRef, useState, forwardRef } from 'react'
import {
  Hint,
  HintContainer,
  InputBase,
  InputComponent,
  InputContainer,
  Label,
  LabelContainer,
  TextAreaBase
} from './styled-components'
import PropTypes from 'prop-types'
import { Cancel } from '../../atoms/icons/essentials'
import {
  TrailingPassword,
  TrailingPasswordHide,
  Mandatory
} from '../../atoms/icons/text-input'
import {
  textInputHintIconsOptions,
  textInputHintVariantOptions,
  textInputHintVariantStyledOptions
} from './constants'
import { iconSizeOptions } from '../../atoms/icons/icons-sizes.js'

const HintComponent = ({ hintMessage, hintVariant, hintShow }) => {
  if (!hintMessage || !hintShow) return null

  const Component = textInputHintVariantStyledOptions[hintVariant] || Hint
  return (
    <HintContainer>
      <Component>{hintMessage}</Component>
    </HintContainer>
  )
}

HintComponent.propTypes = {
  hintMessage: PropTypes.string,
  hintVariant: PropTypes.oneOf(Object.values(textInputHintVariantOptions)),
  hintShow: PropTypes.bool
}

const TextInputComponent = forwardRef(
  (
    {
      label,
      maxCharacters,
      showCounter = true,
      icon,
      rightIcon = null,
      clearButton = false,
      hintMessage,
      hintVariant = textInputHintVariantOptions.BASIC,
      hintShow,
      readOnly,
      type = 'text',
      disabled = false,
      register = {},
      noBorderBottom = false,
      containerStyle,
      onChange: onCustomOnChange = () => {},
      value = null,
      isTextarea = false,
      locked = false,
      focusOnMount = false,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef(null)
    const [showLabel, setShowLabel] = useState(false)
    const [counter, setCounter] = useState(0)
    const [showPassword, setShowPassword] = useState(false)

    const DefineComponent =
      isTextarea && type === 'text' ? TextAreaBase : InputBase

    const inputIsEmpty = inputRef.current && inputRef.current.value.length > 0
    const typePassword = !showPassword ? 'password' : 'text'
    const inputType = type === 'password' ? typePassword : type

    const defineShowCounter = showCounter && maxCharacters

    const handleInputChange = (e) => {
      if (locked) return
      let inputValue = e.target.value
      const characterCount = inputValue.length

      onCustomOnChange && onCustomOnChange(e)

      if (maxCharacters && characterCount > maxCharacters) {
        inputValue = inputValue.substring(0, maxCharacters)
        e.target.value = inputValue
      }

      const shouldShowLabel = inputValue.length > 0

      if (shouldShowLabel !== showLabel) {
        setShowLabel(shouldShowLabel) // update state to force a re-render
      }
      if (maxCharacters) {
        setCounter(inputValue.length)
      }
    }

    const handleOnKeyDown = (e) => {
      if (locked) {
        e.preventDefault()
      }
      inputProps?.onKeyDown?.(e)
    }

    const handleClear = () => {
      if (inputRef && inputRef.current) {
        // Create a synthetic event-like object
        const syntheticEvent = {
          target: {
            value: ''
          }
        }

        // Call the onChange handler with the synthetic event
        onCustomOnChange && onCustomOnChange(syntheticEvent)

        inputRef.current.value = ''

        setCounter(0)
        setShowLabel(false)
        setShowPassword(false)
      }
    }

    const handleHidePassword = () => {
      if (type === 'password') {
        setShowPassword(!showPassword)
      }
    }

    const handleInputRef = (e) => {
      // react-hook-form
      if (register?.ref) {
        register.ref(e)
      }

      // forwardRef
      if (typeof ref === 'function') {
        ref(e)
      } else if (ref) {
        ref.current = e
      }

      // internal ref
      inputRef.current = e
    }

    const passwordToggleComponent = showLabel && type === 'password' && (
      <>
        {showPassword ? (
          <TrailingPasswordHide
            size="icon-s"
            onClick={handleHidePassword}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <TrailingPassword
            size="icon-s"
            onClick={handleHidePassword}
            style={{ cursor: 'pointer' }}
          />
        )}
      </>
    )

    const labelText = inputProps.required ? `${label} *` : label

    useEffect(() => {
      const inputElement = inputRef.current
      if (inputElement) {
        const initialLength = inputElement.value.length
        setCounter(maxCharacters ? initialLength : 0)
        setShowLabel(initialLength > 0)
      }
    }, [maxCharacters])

    const clearButtonComponent = showLabel && clearButton && (
      <Cancel
        size={iconSizeOptions['ICON-XS']}
        onClick={handleClear}
        style={{ cursor: 'pointer' }}
      />
    )

    // useEffect to handle controlled mode
    useEffect(() => {
      if (value !== undefined && inputRef.current) {
        // Component is controlled
        const valueLength = value?.length || 0
        setCounter(maxCharacters ? valueLength : 0)
        setShowLabel(valueLength > 0)
      }
    }, [value, maxCharacters])

    // active label when there is a value
    useEffect(() => {
      if (inputRef) {
        const inputValue = inputRef.current.value

        if (inputValue.length > 0) {
          setShowLabel(true)
        }
      }
    }, [inputRef])

    // focus on mount
    useEffect(() => {
      if (focusOnMount && inputRef.current) {
        inputRef.current.focus()
      }
    }, [])

    // autosize for text area
    useEffect(() => {
      if (isTextarea && inputRef.current) {
        inputRef.current.style.height = '24px'
        inputRef.current.style.height = `${Math.max(24, inputRef.current.scrollHeight)}px`
      }
    }, [inputRef?.current?.value, isTextarea])

    return (
      <InputComponent style={containerStyle}>
        <LabelContainer>
          <Label
            data-disabled={disabled}
            style={{ opacity: showLabel ? '1' : '0' }}
          >
            {label}{' '}
            {inputProps.required && label && <Mandatory size="icon-s" />}
          </Label>
          {defineShowCounter && (
            <Label
              data-disabled={disabled}
              style={{ opacity: showLabel ? '1' : '0' }}
            >
              {counter} / {maxCharacters}
            </Label>
          )}
        </LabelContainer>
        <InputContainer
          isError={
            hintShow && hintVariant === textInputHintVariantOptions.ERROR
          }
          readOnly={readOnly}
          data-disabled={disabled}
          noBorderBottom={noBorderBottom}
        >
          {icon && <div onClick={() => inputRef.current?.focus()}>{icon}</div>}
          <DefineComponent
            locked={locked}
            placeholder={labelText}
            spellCheck="false"
            type={inputType}
            disabled={disabled}
            required={inputProps.required}
            value={value}
            hasValue={inputIsEmpty}
            {...register}
            ref={handleInputRef}
            onChange={(e) => {
              register?.onChange?.(e)
              handleInputChange(e)
            }}
            onKeyDown={handleOnKeyDown}
            {...inputProps}
          />
          {!!rightIcon && !clearButton && (
            <div onClick={() => inputRef.current?.focus()}>{rightIcon}</div>
          )}
          {clearButtonComponent}
          {passwordToggleComponent}
          {hintShow && (textInputHintIconsOptions[hintVariant] ?? null)}
        </InputContainer>
        <HintComponent
          hintMessage={hintMessage}
          hintVariant={hintVariant}
          hintShow={hintShow}
        />
      </InputComponent>
    )
  }
)

TextInputComponent.propTypes = {
  label: PropTypes.string.isRequired,
  maxCharacters: PropTypes.number,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  rightIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  clearButton: PropTypes.bool,
  hintMessage: PropTypes.string,
  hintVariant: PropTypes.oneOf(Object.values(textInputHintVariantOptions)),
  hintShow: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  type: PropTypes.string,
  inputProps: PropTypes.object,
  containerStyle: PropTypes.object,
  onChange: PropTypes.func,
  value: PropTypes.string,
  isTextarea: PropTypes.bool,
  locked: PropTypes.bool,
  focusOnMount: PropTypes.bool
}

TextInputComponent.displayName = 'TextInputComponent'

export default TextInputComponent
